import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Menu,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import {
  Build,
  Add,
  MoreVert,
  Edit,
  Delete,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Pending,
  Search,
  Assignment,
  Engineering,
  Schedule,
} from '@mui/icons-material';

const maintenanceData = [
  {
    id: 'MNT-001',
    title: 'AC Unit Repair - Room 305',
    room: 'DEL-305',
    category: 'HVAC',
    priority: 'High',
    status: 'In Progress',
    assignedTo: 'Mike Technician',
    reportedBy: 'Sarah Manager',
    reportedDate: '2025-11-15',
    scheduledDate: '2025-11-16',
    description: 'Air conditioning not cooling properly. Guest complained of warm room.',
    estimatedCost: 250,
  },
  {
    id: 'MNT-002',
    title: 'Leaking Faucet - Room 102',
    room: 'STD-102',
    category: 'Plumbing',
    priority: 'Medium',
    status: 'Scheduled',
    assignedTo: 'John Plumber',
    reportedBy: 'Lisa Staff',
    reportedDate: '2025-11-14',
    scheduledDate: '2025-11-17',
    description: 'Bathroom sink faucet dripping continuously.',
    estimatedCost: 120,
  },
  {
    id: 'MNT-003',
    title: 'Door Lock Malfunction - Room 204',
    room: 'DEL-204',
    category: 'Security',
    priority: 'High',
    status: 'Pending',
    assignedTo: 'Tom Security',
    reportedBy: 'Sarah Manager',
    reportedDate: '2025-11-16',
    scheduledDate: '2025-11-16',
    description: 'Electronic door lock not functioning. Needs immediate attention.',
    estimatedCost: 180,
  },
  {
    id: 'MNT-004',
    title: 'Broken Window - Lobby',
    room: 'Lobby',
    category: 'General',
    priority: 'Low',
    status: 'Completed',
    assignedTo: 'Mike Technician',
    reportedBy: 'John Admin',
    reportedDate: '2025-11-10',
    scheduledDate: '2025-11-11',
    completedDate: '2025-11-11',
    description: 'Small crack in lobby window. Non-urgent repair.',
    estimatedCost: 300,
    actualCost: 280,
  },
];

export default function Maintenance() {
  const [issues, setIssues] = useState(maintenanceData);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [issueForm, setIssueForm] = useState({
    title: '',
    room: '',
    category: 'General',
    priority: 'Medium',
    status: 'Pending',
    assignedTo: '',
    description: '',
    scheduledDate: '',
    estimatedCost: 0,
  });

  const statusFilters = ['All', 'Pending', 'Scheduled', 'In Progress', 'Completed'];

  const filteredIssues = issues.filter((issue) => {
    const matchesSearch = issue.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          issue.room.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          issue.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 0 || issue.status === statusFilters[activeTab];
    return matchesSearch && matchesTab;
  });

  const stats = [
    {
      label: 'Total Issues',
      value: issues.length,
      color: '#1976d2',
      icon: Build,
    },
    {
      label: 'Pending',
      value: issues.filter(i => i.status === 'Pending').length,
      color: '#ed6c02',
      icon: Pending,
    },
    {
      label: 'In Progress',
      value: issues.filter(i => i.status === 'In Progress').length,
      color: '#9c27b0',
      icon: Engineering,
    },
    {
      label: 'Completed',
      value: issues.filter(i => i.status === 'Completed').length,
      color: '#2e7d32',
      icon: CheckCircle,
    },
  ];

  const handleMenuClick = (event, issue) => {
    setAnchorEl(event.currentTarget);
    setSelectedIssue(issue);
  };

  const handleAddIssue = () => {
    setSelectedIssue(null);
    setIssueForm({
      title: '',
      room: '',
      category: 'General',
      priority: 'Medium',
      status: 'Pending',
      assignedTo: '',
      description: '',
      scheduledDate: '',
      estimatedCost: 0,
    });
    setIssueDialogOpen(true);
  };

  const handleEditIssue = () => {
    setIssueForm({
      title: selectedIssue.title,
      room: selectedIssue.room,
      category: selectedIssue.category,
      priority: selectedIssue.priority,
      status: selectedIssue.status,
      assignedTo: selectedIssue.assignedTo,
      description: selectedIssue.description,
      scheduledDate: selectedIssue.scheduledDate,
      estimatedCost: selectedIssue.estimatedCost,
    });
    setIssueDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSaveIssue = () => {
    if (!issueForm.title || !issueForm.room) {
      setSnackbar({
        open: true,
        message: 'Please fill in required fields',
        severity: 'error',
      });
      return;
    }

    if (selectedIssue) {
      setIssues(issues.map(i => 
        i.id === selectedIssue.id 
          ? { ...i, ...issueForm }
          : i
      ));
      setSnackbar({
        open: true,
        message: 'Maintenance issue updated successfully!',
        severity: 'success',
      });
    } else {
      const newIssue = {
        id: `MNT-${String(issues.length + 1).padStart(3, '0')}`,
        ...issueForm,
        reportedBy: 'Current User',
        reportedDate: new Date().toISOString().split('T')[0],
      };
      setIssues([newIssue, ...issues]);
      setSnackbar({
        open: true,
        message: 'Maintenance issue created successfully!',
        severity: 'success',
      });
    }
    setIssueDialogOpen(false);
  };

  const handleDeleteIssue = () => {
    setIssues(issues.filter(i => i.id !== selectedIssue.id));
    setSnackbar({
      open: true,
      message: 'Maintenance issue deleted',
      severity: 'success',
    });
    setAnchorEl(null);
  };

  const handleViewIssue = () => {
    setViewDialogOpen(true);
    setAnchorEl(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      default: return 'info';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'primary';
      case 'Scheduled': return 'info';
      default: return 'warning';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'High': return <ErrorIcon />;
      case 'Medium': return <Warning />;
      default: return <Assignment />;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Maintenance Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track and manage property maintenance issues
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddIssue}>
          Report Issue
        </Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {stat.label}
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color={stat.color}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                      <IconComponent />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            {statusFilters.map((filter) => (
              <Tab key={filter} label={filter} />
            ))}
          </Tabs>
        </Box>
        <Box p={2}>
          <TextField
            fullWidth
            placeholder="Search by issue, room, or ID..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Issue ID</strong></TableCell>
              <TableCell><strong>Title</strong></TableCell>
              <TableCell><strong>Room</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Priority</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Assigned To</strong></TableCell>
              <TableCell><strong>Scheduled</strong></TableCell>
              <TableCell><strong>Cost</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredIssues.map((issue) => (
              <TableRow key={issue.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Build fontSize="small" color="primary" />
                    <strong>{issue.id}</strong>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {issue.title}
                  </Typography>
                </TableCell>
                <TableCell>{issue.room}</TableCell>
                <TableCell>
                  <Chip label={issue.category} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={issue.priority}
                    size="small"
                    color={getPriorityColor(issue.priority)}
                    icon={getPriorityIcon(issue.priority)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={issue.status}
                    size="small"
                    color={getStatusColor(issue.status)}
                  />
                </TableCell>
                <TableCell>{issue.assignedTo}</TableCell>
                <TableCell>{new Date(issue.scheduledDate).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    ${issue.actualCost || issue.estimatedCost}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={(e) => handleMenuClick(e, issue)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleViewIssue}>
          <Assignment fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditIssue}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit Issue
        </MenuItem>
        <MenuItem onClick={handleDeleteIssue} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete Issue
        </MenuItem>
      </Menu>

      {/* Add/Edit Issue Dialog */}
      <Dialog open={issueDialogOpen} onClose={() => setIssueDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Build color="primary" />
            <Typography variant="h6">{selectedIssue ? 'Edit Maintenance Issue' : 'Report New Issue'}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Issue Title"
                value={issueForm.title}
                onChange={(e) => setIssueForm({ ...issueForm, title: e.target.value })}
                required
                placeholder="e.g., AC Unit Repair - Room 305"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Room/Location"
                value={issueForm.room}
                onChange={(e) => setIssueForm({ ...issueForm, room: e.target.value })}
                required
                placeholder="e.g., STD-102 or Lobby"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={issueForm.category}
                onChange={(e) => setIssueForm({ ...issueForm, category: e.target.value })}
              >
                <MenuItem value="HVAC">HVAC</MenuItem>
                <MenuItem value="Plumbing">Plumbing</MenuItem>
                <MenuItem value="Electrical">Electrical</MenuItem>
                <MenuItem value="Security">Security</MenuItem>
                <MenuItem value="Furniture">Furniture</MenuItem>
                <MenuItem value="General">General</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Priority"
                value={issueForm.priority}
                onChange={(e) => setIssueForm({ ...issueForm, priority: e.target.value })}
              >
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Status"
                value={issueForm.status}
                onChange={(e) => setIssueForm({ ...issueForm, status: e.target.value })}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Assigned To"
                value={issueForm.assignedTo}
                onChange={(e) => setIssueForm({ ...issueForm, assignedTo: e.target.value })}
                placeholder="Technician name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Scheduled Date"
                type="date"
                value={issueForm.scheduledDate}
                onChange={(e) => setIssueForm({ ...issueForm, scheduledDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Estimated Cost"
                type="number"
                value={issueForm.estimatedCost}
                onChange={(e) => setIssueForm({ ...issueForm, estimatedCost: parseFloat(e.target.value) })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={issueForm.description}
                onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })}
                placeholder="Detailed description of the issue..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveIssue}>
            {selectedIssue ? 'Update Issue' : 'Create Issue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Issue Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Build color="primary" />
            <Typography variant="h6">Maintenance Issue Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedIssue && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Issue ID</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedIssue.id}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedIssue.status}
                  size="small"
                  color={getStatusColor(selectedIssue.status)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Title</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedIssue.title}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Room/Location</Typography>
                <Typography variant="body1">{selectedIssue.room}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Category</Typography>
                <Chip label={selectedIssue.category} size="small" variant="outlined" />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Priority</Typography>
                <Chip
                  label={selectedIssue.priority}
                  size="small"
                  color={getPriorityColor(selectedIssue.priority)}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Assigned To</Typography>
                <Typography variant="body1">{selectedIssue.assignedTo}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Reported By</Typography>
                <Typography variant="body1">{selectedIssue.reportedBy}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Reported Date</Typography>
                <Typography variant="body1">{new Date(selectedIssue.reportedDate).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Scheduled Date</Typography>
                <Typography variant="body1">{new Date(selectedIssue.scheduledDate).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Estimated Cost</Typography>
                <Typography variant="body1" fontWeight="bold">${selectedIssue.estimatedCost}</Typography>
              </Grid>
              {selectedIssue.completedDate && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Completed Date</Typography>
                  <Typography variant="body1">{new Date(selectedIssue.completedDate).toLocaleDateString()}</Typography>
                </Grid>
              )}
              {selectedIssue.actualCost && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Actual Cost</Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">
                    ${selectedIssue.actualCost}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Description</Typography>
                <Typography variant="body1">{selectedIssue.description}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<Edit />} onClick={() => { setViewDialogOpen(false); handleEditIssue(); }}>
            Edit Issue
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
